import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRightLeft, Trash2, PackageCheck, Camera } from "lucide-react";
import { Modal, In, Sel } from "@/pages/Projects";
import EvidenceGallery from "@/components/EvidenceGallery";

export default function TransfersTab({ warehouses, products, canManage, onChanged }) {
  const [transfers, setTransfers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [receiving, setReceiving] = useState(null); // transfer doc
  const [gallery, setGallery] = useState(null);
  const [form, setForm] = useState({ source_warehouse_id: "", dest_warehouse_id: "", notes: "", items: [{ product_id: "", quantity: "" }] });
  const [recForm, setRecForm] = useState({ items: [], notes: "" });
  const [recFiles, setRecFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/stock-transfers");
    setTransfers(data);
  };
  useEffect(() => { load(); }, []);

  const uploadPhotos = async (files) => {
    const ids = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      ids.push(data.id);
    }
    return ids;
  };

  const startNew = () => {
    setForm({ source_warehouse_id: warehouses[0]?.id || "", dest_warehouse_id: warehouses[1]?.id || "", notes: "", items: [{ product_id: products[0]?.id || "", quantity: "" }] });
    setOpenNew(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/stock-transfers", {
        source_warehouse_id: form.source_warehouse_id,
        dest_warehouse_id: form.dest_warehouse_id,
        notes: form.notes || null,
        items: form.items.filter((i) => i.product_id && Number(i.quantity) > 0).map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity) })),
      });
      toast.success("Transferencia enviada (en tránsito)");
      setOpenNew(false); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const startReceive = (t) => {
    setReceiving(t);
    setRecForm({ items: t.items.map((it) => ({ product_id: it.product_id, product_name: it.product_name, unit: it.unit, qty_sent: it.qty_sent, qty_received: it.qty_sent })), notes: "" });
    setRecFiles([]);
  };

  const confirmReceive = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photos = recFiles.length ? await uploadPhotos(recFiles) : [];
      const { data } = await api.post(`/stock-transfers/${receiving.id}/receive`, {
        items: recForm.items.map((i) => ({ product_id: i.product_id, qty_received: Number(i.qty_received) })),
        notes: recForm.notes || null, photos,
      });
      toast.success(data.status === "recibida_con_diferencias" ? "Recibida CON diferencias registradas" : "Transferencia recibida completa");
      setReceiving(null); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const cancel = async (t) => {
    if (!window.confirm(`¿Cancelar ${t.folio}? El material regresa al almacén origen.`)) return;
    try { await api.post(`/stock-transfers/${t.id}/cancel`); toast.success("Cancelada, stock devuelto a origen"); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const statusChip = (s) => {
    const map = {
      en_transito: ["EN TRÁNSITO", "var(--yellow, #eab308)"],
      recibida: ["RECIBIDA", "var(--green)"],
      recibida_con_diferencias: ["CON DIFERENCIAS", "var(--red)"],
      cancelada: ["CANCELADA", "var(--text-dim)"],
    };
    const [label, color] = map[s] || [s, "var(--cyan)"];
    return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ border: `1px solid ${color}`, color }}>{label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="transfers-tab">
      <div className="flex justify-between items-center">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// TRANSFERENCIAS ENTRE ALMACENES Y OBRAS · no generan compras ni gastos</span>
        <button data-testid="new-transfer-btn" onClick={startNew} disabled={warehouses.length < 2 || !products.length} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50">
          <Plus className="w-3 h-3" /> Transferir
        </button>
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[950px]" data-testid="transfers-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-4 py-3">Folio</th><th className="label-mono px-4 py-3">Origen → Destino</th>
            <th className="label-mono px-4 py-3">Partidas</th><th className="label-mono px-4 py-3">Estado</th>
            <th className="label-mono px-4 py-3">Envió</th><th className="label-mono px-4 py-3">Recibió</th>
            <th className="label-mono px-4 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {transfers.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500">Sin transferencias.</td></tr>}
            {transfers.map((t) => (
              <tr key={t.id} className="row-hover align-top" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-4 py-3 font-mono text-xs font-bold" style={{ color: "var(--cyan)" }}>{t.folio}</td>
                <td className="px-4 py-3 text-xs"><span className="text-slate-300">{t.source_warehouse_name}</span> <ArrowRightLeft className="w-3 h-3 inline mx-1 text-slate-500" /> <span className="font-semibold text-slate-100">{t.dest_warehouse_name}</span></td>
                <td className="px-4 py-3 text-xs text-slate-300">
                  {t.items.map((it) => (
                    <div key={it.product_id} className="whitespace-nowrap">
                      {it.product_name}: <span className="font-mono">{it.qty_sent}</span>
                      {it.qty_received != null && <span className="font-mono"> → {it.qty_received} {it.unit}{Math.abs(it.difference) > 0.001 && <span style={{ color: "var(--red)" }}> (dif {it.difference})</span>}</span>}
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3">{statusChip(t.status)}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{t.sent_by_name}<br />{fmtDate(t.sent_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{t.received_by_name ? <>{t.received_by_name}<br />{fmtDate(t.received_at)}</> : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {(t.photos?.length > 0 || t.receive_photos?.length > 0) && (
                    <button data-testid={`transfer-photos-${t.id}`} onClick={() => setGallery({ photos: [...(t.photos || []), ...(t.receive_photos || [])], title: t.folio })} className="p-1 mr-2 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Camera className="w-4 h-4" /></button>
                  )}
                  {t.status === "en_transito" && (
                    <>
                      <button data-testid={`receive-transfer-${t.id}`} onClick={() => startReceive(t)} className="text-xs font-semibold hover:underline mr-3 inline-flex items-center gap-1" style={{ color: "var(--green)" }}><PackageCheck className="w-3.5 h-3.5" /> RECIBIR</button>
                      {canManage && <button data-testid={`cancel-transfer-${t.id}`} onClick={() => cancel(t)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && (
        <Modal onClose={() => setOpenNew(false)} title="Nueva transferencia">
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Almacén origen" testId="tr-source" value={form.source_warehouse_id} onChange={(v) => setForm({ ...form, source_warehouse_id: v })} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} />
              <Sel label="Almacén destino" testId="tr-dest" value={form.dest_warehouse_id} onChange={(v) => setForm({ ...form, dest_warehouse_id: v })} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} />
            </div>
            <div className="space-y-2">
              <span className="label-mono">Partidas</span>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_110px_32px] gap-2 items-end">
                  <Sel label={idx === 0 ? "Producto" : ""} testId={`tr-prod-${idx}`} value={it.product_id} onChange={(v) => { const items = [...form.items]; items[idx].product_id = v; setForm({ ...form, items }); }} options={products.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
                  <In label={idx === 0 ? "Cantidad" : ""} testId={`tr-qty-${idx}`} type="number" value={it.quantity} onChange={(v) => { const items = [...form.items]; items[idx].quantity = v; setForm({ ...form, items }); }} />
                  <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} disabled={form.items.length === 1} className="p-2 rounded-sm hover:bg-white/5 disabled:opacity-30" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" data-testid="tr-add-item" onClick={() => setForm({ ...form, items: [...form.items, { product_id: products[0]?.id || "", quantity: "" }] })} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Partida</button>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <p className="text-xs text-slate-400">El stock sale del origen al confirmar. El destino debe confirmar la recepción; las diferencias se registran.</p>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenNew(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-transfer-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Enviar transferencia</button>
            </div>
          </form>
        </Modal>
      )}

      {receiving && (
        <Modal onClose={() => setReceiving(null)} title={`Recibir ${receiving.folio}`}>
          <form onSubmit={confirmReceive} className="space-y-4">
            <div className="space-y-2">
              {recForm.items.map((it, idx) => (
                <div key={it.product_id} className="grid grid-cols-[1fr_130px] gap-2 items-center">
                  <div className="text-sm text-slate-200">{it.product_name} <span className="text-xs text-slate-500">(enviado: {it.qty_sent} {it.unit})</span></div>
                  <In label="" testId={`rec-qty-${idx}`} type="number" value={it.qty_received} onChange={(v) => { const items = [...recForm.items]; items[idx].qty_received = v; setRecForm({ ...recForm, items }); }} />
                </div>
              ))}
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fotos de recepción (opcional)</label>
              <input data-testid="rec-photos" type="file" accept="image/*" multiple onChange={(e) => setRecFiles([...e.target.files])} className="text-xs text-slate-400" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={recForm.notes} onChange={(e) => setRecForm({ ...recForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <p className="text-xs" style={{ color: "var(--yellow, #eab308)" }}>Si recibes menos de lo enviado, la diferencia queda registrada en la transferencia.</p>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setReceiving(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="confirm-receive-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Confirmar recepción</button>
            </div>
          </form>
        </Modal>
      )}

      {gallery && <EvidenceGallery photos={gallery.photos} title={gallery.title} onClose={() => setGallery(null)} />}
    </div>
  );
}
